import { useEffect, useRef, useState } from "react";

import Footer from "../Components/Footer/Footer";
import Header from "../Components/Header/Header";
import HeaderWhite from "../Components/Header/HeaderWhite";
import { homePage } from "../actions/ApiCalls";
import "./Home.css";

import { IMAGE_URL } from "../path";
import { Link } from "react-router-dom";

type BrandScrollerItem = {
    id: number;
    name: string;
    search_name: string;
    hero_images: {
        id: number;
        lifestyle_image: string;
    }[];
};

type HomeSection = {
    id: number;
    title: string | null;
    image: string | null;
    left_text: string | null;
    right_text: string | null;
    position: number;
};

type CoreCapability = {
    id: number;
    image: string | null;
    top_text: string | null;
    bottom_text: string | null;
    right_text: string | null;
    position: number;
};

type HomePageData = {
    sections: HomeSection[];
    capabilities: CoreCapability[];
    left: BrandScrollerItem[];
    right: BrandScrollerItem[];
};

function Home() {
    const [isAtTop, setIsAtTop] = useState(true);

    const [homeData, setHomeData] = useState<HomePageData>({
        sections: [],
        capabilities: [],
        left: [],
        right: [],
    });

    const imageUrl = IMAGE_URL;

    const warehousingRef = useRef<HTMLDivElement>(null);
    const leftPanelRef = useRef<HTMLDivElement>(null);

    const [panelMode, setPanelMode] = useState<
        "absolute" | "fixed"
    >("absolute");

    const [fixedTop, setFixedTop] = useState(120);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        homePage()
            .then(response => {
                setHomeData(response.data);
            })
            .catch(error =>
                console.error(
                    "Failed to fetch home page:",
                    error
                )
            );
    }, []);

    const getSection = (position: number) => {
        return homeData.sections.find(
            section => section.position === position
        );
    };

    const hero = getSection(1);
    const intro = getSection(2);
    const capabilitiesSection = getSection(3);
    const coreCapabilitiesSection = getSection(4);
    const portfolioSection = getSection(5);
    const futureSection = getSection(6);
    const growBrandSection = getSection(7);
    const viewPortfolioSection = getSection(8);

    const handleWarehousingScroll = () => {
        if (
            !warehousingRef.current ||
            !leftPanelRef.current
        ) {
            return;
        }

        const section = warehousingRef.current;
        const panel = leftPanelRef.current;

        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const panelHeight = panel.offsetHeight;

        const scrollY = window.scrollY;

        const fixedTop = 120;
        const absoluteTop = 100;
        const bottomPadding = 100;

        const start =
            sectionTop +
            absoluteTop -
            fixedTop;

        const end =
            sectionTop +
            sectionHeight -
            panelHeight -
            fixedTop -
            bottomPadding;

        if (scrollY < start) {
            setPanelMode("absolute");
            setFixedTop(absoluteTop);
        } else {
            setPanelMode("fixed");

            const top =
                scrollY < end
                    ? fixedTop
                    : fixedTop - (scrollY - end);

            setFixedTop(top);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsAtTop(window.scrollY === 0);
            handleWarehousingScroll();
        };

        window.addEventListener("scroll", handleScroll);

        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}
        >
            <div className="header-container">
                {isAtTop ? (
                    <HeaderWhite textColor="black" />
                ) : (
                    <Header />
                )}
            </div>

            <div className="home-page-container">

                {/* HERO */}
                <div className="home-banner">
                    {hero?.image && (
                        <img
                            src={`${imageUrl}${hero.image}`}
                            alt=""
                            fetchPriority="high"
                            decoding="async"
                        />
                    )}

                    <div className="home-banner-bottom">
                        <div className="home-banner-bottom-p">
                            SCROLL DOWN
                        </div>

                        <div className="home-banner-bottom-svg">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17.121"
                                height="14.81"
                                viewBox="0 0 17.121 14.81"
                            >
                                <path
                                    id="Path_1"
                                    data-name="Path 1"
                                    d="M4.5,5.25,12,12.472l7.5-7.222m-15,5.778L12,18.25l7.5-7.222"
                                    transform="translate(-3.44 -4.19)"
                                    fill="none"
                                    stroke="#fff"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* INTRO */}
                <div className="home-portfolio-section">
                    <h1>
                        {intro?.title}
                    </h1>

                    <div className="home-portfolio-section-link">
                        <Link
                            to={"/our-wines"}
                            className="our-wines-link"
                        >
                            VIEW OUR PORTFOLIO
                        </Link>
                    </div>
                </div>

                {/* OUR CAPABILITIES */}
                <div className="home-capabilities-section">

                    <div className="home-capabilities-image">
                        {capabilitiesSection?.image && (
                            <img
                                src={`${imageUrl}${capabilitiesSection.image}`}
                                alt={
                                    capabilitiesSection.title ||
                                    "Our Capabilities"
                                }
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </div>

                    <div className="home-capabilities-body">
                        <h2>
                            {capabilitiesSection?.title}
                        </h2>

                        <p>
                            {capabilitiesSection?.left_text}
                        </p>

                        {/* Hardcoded CTA */}
                        <div className="our-story-link-box">
                            <Link
                                className="our-story-link"
                                to={"about/our-story"}
                            >
                                OUR STORY
                            </Link>
                        </div>
                    </div>

                </div>

                {/* CORE CAPABILITIES */}
                <div
                    ref={warehousingRef}
                    className="home-warehousing-section"
                >

                    <div
                        ref={leftPanelRef}
                        className={`home-warehousing-left ${panelMode}`}
                        style={
                            panelMode === "fixed"
                                ? {
                                    top: `${fixedTop}px`,
                                }
                                : undefined
                        }
                    >
                        <h2 className="home-warehousing-item-title">
                            CORE CAPABILITIES
                        </h2>

                        <h1 className="home-warehousing-item-heading">
                            {coreCapabilitiesSection?.title}
                        </h1>

                        <p>
                            {coreCapabilitiesSection?.left_text}
                        </p>
                    </div>

                    <div className="home-warehousing-right">

                        {homeData.capabilities.map(
                            capability => (
                                <div
                                    className="home-warehousing-row"
                                    key={capability.id}
                                >

                                    <div
                                        className="home-warehousing-item-image"
                                        style={
                                            capability.image
                                                ? {
                                                    backgroundImage: `url(${imageUrl}${capability.image})`,
                                                }
                                                : undefined
                                        }
                                    >
                                        <p className="image-row-1">
                                            {capability.top_text}
                                        </p>

                                        <p className="image-row-2">
                                            {capability.bottom_text}
                                        </p>
                                    </div>

                                    <div className="home-warehousing-item-right">
                                        <p>
                                            {capability.right_text}
                                        </p>

                                        {/* Hardcoded CTA */}
                                        <div className="home-warehousing-item-link-box">
                                            <Link
                                                to="/about/partnership#warehousing--distribution"
                                                className="home-warehousing-item-link"
                                            >
                                                WAREHOUSING & DISTRIBUTION
                                            </Link>
                                        </div>
                                    </div>

                                </div>
                            )
                        )}

                    </div>

                </div>

                {/* PORTFOLIO */}
                <div className="home-brands-section">

                    <div className="home-brands-top">

                        <div className="home-brands-top-left">
                            <h2>
                                THE PORTFOLIO
                            </h2>

                            <h1>
                                {portfolioSection?.left_text}
                            </h1>
                        </div>

                        <div className="home-brands-top-mid"></div>

                        <div className="home-brands-top-right">
                            <p>
                                {portfolioSection?.right_text}
                            </p>

                            {/* Hardcoded CTA */}
                            <div className="home-brands-link-box">
                                <Link
                                    to={"/our-wines"}
                                    className="home-brands-link"
                                >
                                    VIEW PORTFOLIO
                                </Link>
                            </div>
                        </div>

                    </div>

                    <div className="home-brands-carousel">

                        <div className="brands-track brands-track-top">
                            {[
                                ...(homeData.left ?? []),
                                ...(homeData.left ?? []),
                            ].map(
                                (brand, index) => (
                                    <Link
                                        key={`top-${index}`}
                                        to={`/our-wines/${brand.search_name}`}
                                        className="brand-carousel-item"
                                    >
                                        {brand.hero_images?.[0]
                                            ?.lifestyle_image && (
                                            <img
                                                src={`${IMAGE_URL}${brand.hero_images[0].lifestyle_image}`}
                                                alt={brand.name}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        )}
                                    </Link>
                                )
                            )}
                        </div>

                        <div className="brands-track brands-track-bottom">
                            {[
                                ...(homeData.right ?? []),
                                ...(homeData.right ?? []),
                            ].map(
                                (brand, index) => (
                                    <Link
                                        key={`bottom-${index}`}
                                        to={`/our-wines/${brand.search_name}`}
                                        className="brand-carousel-item"
                                    >
                                        {brand.hero_images?.[0]
                                            ?.lifestyle_image && (
                                            <img
                                                src={`${IMAGE_URL}${brand.hero_images[0].lifestyle_image}`}
                                                alt={brand.name}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        )}
                                    </Link>
                                )
                            )}
                        </div>

                    </div>

                </div>

                {/* WE ARE INVESTED IN YOUR FUTURE */}
                <div
                    className="home-future-section"
                    style={
                        futureSection?.image
                            ? {
                                backgroundImage: `url(${imageUrl}${futureSection.image})`,
                            }
                            : undefined
                    }
                >
                    <h1>
                        {futureSection?.title}
                    </h1>

                    {/* Hardcoded CTA */}
                    <div className="home-future-link-box">
                        <Link
                            className="home-future-link"
                            to={"/our-wines"}
                        >
                            VIEW OUR PORTFOLIO
                        </Link>
                    </div>
                </div>

                {/* READY TO GROW YOUR BRAND */}
                <div className="home-grow-brand-section">

                    <div className="home-grow-brand-image">
                        {growBrandSection?.image && (
                            <img
                                src={`${imageUrl}${growBrandSection.image}`}
                                alt={
                                    growBrandSection.title ||
                                    "Ready to grow your brand"
                                }
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </div>

                    <div className="home-grow-brand-body">

                        <div className="home-grow-brand-top">
                            <h1>
                                {growBrandSection?.title}
                            </h1>

                            {/* Hardcoded CTA */}
                            <div className="home-grow-brand-top-link-box">
                                <Link
                                    to="/contact"
                                    className="home-grow-brand-top-link"
                                >
                                    LIST YOUR BRAND
                                </Link>
                            </div>
                        </div>

                        <div className="home-grow-brand-bottom">
                        </div>

                    </div>

                </div>

                {/* VIEW OUR PORTFOLIO */}
                <div className="home-view-portfolio">

                    <div className="home-view-portfolio-body">

                        <h1>
                            {viewPortfolioSection?.title}
                        </h1>

                        {/* Hardcoded CTA */}
                        <div className="home-view-portfolio-link-box">
                            <Link
                                to={"/our-wines"}
                                className="home-view-portfolio-link"
                            >
                                LOCAL PORTFOLIO
                            </Link>
                        </div>

                        {/* Hardcoded CTA */}
                        <div className="home-view-portfolio-link-box">
                            <Link
                                to={"/about/international"}
                                className="home-view-portfolio-link"
                            >
                                INTERNATIONAL PORTFOLIO
                            </Link>
                        </div>

                    </div>

                    <div className="home-view-portfolio-image">
                        {viewPortfolioSection?.image && (
                            <img
                                src={`${imageUrl}${viewPortfolioSection.image}`}
                                alt={
                                    viewPortfolioSection.title ||
                                    "View Portfolio"
                                }
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </div>

                </div>

            </div>

            <Footer />
        </div>
    );
}

export default Home;
